import { APPENDIX_TOKENS, mmToPt } from './appendixTokens';
import {
  DEFAULT_APPENDIX_ASPECT,
  ImageAppendixBlockData,
  ImageAppendixItem,
  ImageAspect,
  PhotoPageLayout,
} from './config';

/**
 * The photo appendix's pages and grid -- one module both surfaces read, so the
 * editor page and the PDF page cannot disagree about which photos a page holds
 * or where each one sits (parity rule 1 in DEVELOPMENT.md). Points throughout,
 * as in appendixTokens: react-pdf's unit, and one CSS understands natively.
 *
 * A page's layout is the appendix's (columns, rows, aspect) unless a page
 * override starts at one of its photos; a photo's frame takes the page's
 * aspect unless the photo has its own. See "The photo appendix: a page may
 * have its own layout, a photo its own aspect" under "Appendix blocks own
 * their pages" in DEVELOPMENT.md.
 */

export const ASPECT_OPTIONS: { value: ImageAspect; label: string; short: string }[] = [
  { value: 'horizontal-4-3', label: 'Horizontal 4:3', short: '4:3' },
  { value: 'horizontal-16-9', label: 'Horizontal 16:9', short: '16:9' },
  { value: 'vertical-3-4', label: 'Vertical 3:4', short: '3:4' },
  { value: 'vertical-9-16', label: 'Vertical 9:16', short: '9:16' },
  { value: 'square', label: 'Square', short: '1:1' },
];

/** Width over height. */
export const ASPECT_RATIO: Record<ImageAspect, number> = {
  'horizontal-4-3': 4 / 3,
  'horizontal-16-9': 16 / 9,
  'vertical-3-4': 3 / 4,
  'vertical-9-16': 9 / 16,
  'square': 1,
};

export const aspectOption = (aspect: ImageAspect) =>
  ASPECT_OPTIONS.find(opt => opt.value === aspect) ?? ASPECT_OPTIONS[2];

type Orientation = 'landscape' | 'portrait' | 'square';

const orientationOf = (aspect: ImageAspect): Orientation =>
  aspect === 'square' ? 'square' : aspect.startsWith('horizontal') ? 'landscape' : 'portrait';

/** The preset closest to a photo's own width / height, compared as ratios. */
export const nearestAspect = (ratio: number): ImageAspect => {
  let best: ImageAspect = DEFAULT_APPENDIX_ASPECT;
  let bestDistance = Infinity;
  for (const opt of ASPECT_OPTIONS) {
    const distance = Math.abs(Math.log(ratio / ASPECT_RATIO[opt.value]));
    if (distance < bestDistance) {
      bestDistance = distance;
      best = opt.value;
    }
  }
  return best;
};

/**
 * The aspect to give a photo just uploaded onto a page of `pageAspect`: its
 * own nearest preset when it is turned the other way (a landscape shot on a
 * portrait page), which a page-aspect frame would crop to a sliver; otherwise
 * none, so it takes the page's aspect exactly as before the setting existed.
 */
export const uploadAspectFor = (ratio: number, pageAspect: ImageAspect): ImageAspect | undefined => {
  const own = nearestAspect(ratio);
  return orientationOf(own) === orientationOf(pageAspect) ? undefined : own;
};

// =======================================
// PAGES
// =======================================

export const appendixDefaultLayout = (data: ImageAppendixBlockData): PhotoPageLayout => ({
  columns: data.columns || 2,
  rows: data.rows || 2,
  aspect: data.aspect || DEFAULT_APPENDIX_ASPECT,
});

/** Which photos one page of a photo appendix shows, and how. */
export interface PhotoFragment {
  /** Index into the appendix's images of the page's first photo. */
  start: number;
  /** One past its last photo. */
  end: number;
  layout: PhotoPageLayout;
  /** True when the layout is this page's own, not the appendix's. */
  custom: boolean;
}

/**
 * The appendix's pages. A page holds columns x rows photos of its layout, and
 * a page override (keyed on a photo id) starts a new page at that photo even
 * when the page before it has room -- so the override stays with the photos
 * it was made for when a photo earlier in the appendix is removed. Never
 * empty: an appendix with no photos is one (empty) page.
 */
export const photoAppendixPages = (data: ImageAppendixBlockData): PhotoFragment[] => {
  const images = data.images || [];
  const overrides = data.pageLayouts || {};
  const fallback = appendixDefaultLayout(data);
  const pages: PhotoFragment[] = [];
  let start = 0;
  while (start < images.length) {
    const own = overrides[images[start].id];
    const layout = own ?? fallback;
    const perPage = layout.columns * layout.rows;
    let end = start + 1;
    while (end < images.length && end - start < perPage && !overrides[images[end].id]) end++;
    pages.push({ start, end, layout, custom: !!own });
    start = end;
  }
  return pages.length > 0 ? pages : [{ start: 0, end: 0, layout: fallback, custom: false }];
};

type PageLayouts = NonNullable<ImageAppendixBlockData['pageLayouts']>;

const tidy = (layouts: PageLayouts): PageLayouts | undefined =>
  Object.keys(layouts).length > 0 ? layouts : undefined;

/**
 * Page layouts after the photos were reordered: each stays at its POSITION,
 * so moving a page's first photo moves the photo, not the page's layout. A
 * landscape page whose first photo was nudged up a place is still the
 * landscape page.
 */
export const keepPageLayoutsInPlace = (
  layouts: PageLayouts | undefined,
  before: ImageAppendixItem[],
  after: ImageAppendixItem[]
): PageLayouts | undefined => {
  if (!layouts) return undefined;
  const next: PageLayouts = {};
  before.forEach((img, i) => {
    const layout = layouts[img.id];
    if (layout && after[i]) next[after[i].id] = layout;
  });
  return tidy(next);
};

/**
 * Page layouts after a photo was removed: one that started at it passes to the
 * next photo ON THE SAME PAGE, which now starts that page; every other one
 * stays on its own photo. When the removed photo was its page's only one the
 * page is gone and so is its layout -- handed on regardless, it turned the
 * next page, laid out as the appendix, into a copy of the deleted one.
 */
export const handOnPageLayouts = (
  data: ImageAppendixBlockData,
  removedId: string
): PageLayouts | undefined => {
  const layouts = data.pageLayouts;
  if (!layouts) return undefined;
  const before = data.images || [];
  const next: PageLayouts = {};
  before.forEach(img => {
    if (img.id !== removedId && layouts[img.id]) next[img.id] = layouts[img.id];
  });
  const removed = layouts[removedId];
  const index = before.findIndex(img => img.id === removedId);
  const page = photoAppendixPages(data).find(p => p.start <= index && index < p.end);
  if (removed && page && index + 1 < page.end) next[before[index + 1].id] = removed;
  return tidy(next);
};

// =======================================
// GRID GEOMETRY (points)
// =======================================

/**
 * The appendix title's box: one line (it is cut with an ellipsis, never
 * wrapped, on both surfaces), its padding and the gap below -- the same as
 * the location plan's.
 */
export const PHOTO_TITLE_BLOCK_PT =
  APPENDIX_TOKENS.titleSizePt * APPENDIX_TOKENS.titleLineHeight
  + APPENDIX_TOKENS.titlePadPt * 2
  + APPENDIX_TOKENS.titleGapPt;

const LABEL_BLOCK_PT = APPENDIX_TOKENS.photoLabelGapPt
  + APPENDIX_TOKENS.photoLabelLines * APPENDIX_TOKENS.photoLabelSizePt * APPENDIX_TOKENS.photoLabelLineHeight;

export interface PhotoGrid {
  columns: number;
  rows: number;
  cellWidthPt: number;
  cellHeightPt: number;
  gapPt: number;
  /**
   * The page aspect's frame, fitted into a cell above its label and set at the
   * cell's top left. Its height bounds every photo on the page, so the labels
   * of a row line up whatever each photo's own shape; a photo with its own
   * aspect may run wider than it, up to the cell's width (photoFrame).
   */
  slotWidthPt: number;
  slotHeightPt: number;
}

export const photoGrid = (layout: PhotoPageLayout, hasTitle: boolean): PhotoGrid => {
  const { columns, rows } = layout;
  const gapPt = APPENDIX_TOKENS.photoGapPt;
  const contentWidthPt = mmToPt(APPENDIX_TOKENS.contentWidthMm);
  const contentHeightPt = mmToPt(APPENDIX_TOKENS.contentHeightMm) - (hasTitle ? PHOTO_TITLE_BLOCK_PT : 0);
  const cellWidthPt = (contentWidthPt - gapPt * (columns - 1)) / columns;
  const cellHeightPt = (contentHeightPt - gapPt * (rows - 1)) / rows;
  const slot = fitAspect(ASPECT_RATIO[layout.aspect], cellWidthPt, Math.max(cellHeightPt - LABEL_BLOCK_PT, 0));
  return { columns, rows, cellWidthPt, cellHeightPt, gapPt, slotWidthPt: slot.widthPt, slotHeightPt: slot.heightPt };
};

const fitAspect = (ratio: number, boxWidthPt: number, boxHeightPt: number) => {
  let widthPt = boxWidthPt;
  let heightPt = boxWidthPt / ratio;
  if (heightPt > boxHeightPt) {
    heightPt = boxHeightPt;
    widthPt = boxHeightPt * ratio;
  }
  return { widthPt, heightPt };
};

/**
 * One photo's frame, relative to the slot's top left: the slot itself for a
 * photo with no aspect of its own; else its aspect fitted into the CELL's
 * width and the slot's height, at the cell's left like every other photo and
 * centred top to bottom. The image covers the frame, so a frame of the
 * photo's own shape shows the whole photo. Left, not centred across: a
 * one-column page sets its photos at the left margin, and a landscape photo
 * centred among them read as out of line.
 *
 * The cell's width, not the slot's: a portrait slot on a one-column page is
 * limited by the page height and is about half the page wide, and a landscape
 * photo fitted into it came out the width of a two-column photo. Across the
 * cell it is as wide as on a landscape page. The slot's height still bounds
 * it, so the labels of a row stay level. On a page of two or more columns the
 * slot is the cell's width already (on the title page, within ~1pt), so
 * there it barely moves.
 */
export const photoFrame = (grid: PhotoGrid, aspect: ImageAspect | undefined) => {
  if (!aspect) return { widthPt: grid.slotWidthPt, heightPt: grid.slotHeightPt, leftPt: 0, topPt: 0 };
  const { widthPt, heightPt } = fitAspect(ASPECT_RATIO[aspect], grid.cellWidthPt, grid.slotHeightPt);
  return {
    widthPt,
    heightPt,
    leftPt: 0,
    topPt: (grid.slotHeightPt - heightPt) / 2,
  };
};

/**
 * How wide a photo's "Photo n: caption" line is: the slot's width, or to the
 * frame's right edge when the photo runs wider than the slot (a landscape
 * photo on a one-column portrait page), so its caption is not squeezed into
 * the narrow column beside it.
 */
export const photoLabelWidth = (grid: PhotoGrid, frame: ReturnType<typeof photoFrame>): number =>
  Math.max(grid.slotWidthPt, frame.leftPt + frame.widthPt);
